import { Type, type Static } from '@sinclair/typebox';
import { IsoDateTime } from './common';
import { StringEnum, createSchema } from '../../util/schema';

export const COMPANY_KEY_PATTERN = '^c_[0-9a-hjkmnp-tv-z]{26}$';
export const PARTNER_USER_ID_PATTERN = '^u_[0-9a-hjkmnp-tv-z]{16}_[0-9a-hjkmnp-tv-z]{16}$';

export const PartnerUserId = Type.String({
  pattern: PARTNER_USER_ID_PATTERN,
  description: 'Partner user id: u_{companyHash}_{uniqueHash}. SOAP UserIDs stay decimal digits.',
});
export type PartnerUserId = Static<typeof PartnerUserId>;

export const ActorUserId = Type.Union([Type.Integer({ minimum: 1 }), PartnerUserId], {
  $id: 'ActorUserId',
  description: 'SOAP HedgeFacts UserID or partner u_{companyHash}_{uniqueHash}.',
});
export type ActorUserId = Static<typeof ActorUserId>;

export const CompanyKey = Type.String({
  pattern: COMPANY_KEY_PATTERN,
  description: 'Generated company/tenant id. Doubles as the datasource value for partner tenants.',
});
export type CompanyKey = Static<typeof CompanyKey>;

export const Role = StringEnum(['owner', 'admin', 'user'], {
  $id: 'Role',
  description: 'Partner role. Enforced only on company-admin routes.',
});
export type Role = Static<typeof Role>;

export const UserStructures = Type.Object(
  {
    funds: Type.Array(Type.String()),
    managedAccounts: Type.Array(Type.String()),
    returnsStreams: Type.Array(Type.String()),
  },
  { $id: 'UserStructures' },
);
export type UserStructures = Static<typeof UserStructures>;

export const emptyUserStructures = (): UserStructures => ({
  funds: [],
  managedAccounts: [],
  returnsStreams: [],
});

export const Company = Type.Object(
  {
    companyKey: CompanyKey,
    name: Type.String({ minLength: 1 }),
    externalCompanyId: Type.Optional(Type.Integer({ description: "Partner's own company id. Never used for authorization." })),
    referralCompanyKey: Type.Optional(CompanyKey),
    isAggregator: Type.Optional(Type.Boolean({ description: 'May refer/own other companies (9AT).' })),
    addr1: Type.Optional(Type.String()),
    addr2: Type.Optional(Type.String()),
    city: Type.Optional(Type.String()),
    state: Type.Optional(Type.String()),
    postalCode: Type.Optional(Type.String()),
    phone: Type.Optional(Type.String()),
    contactName: Type.Optional(Type.String()),
    contactEmail: Type.Optional(Type.String({ format: 'email' })),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    createdBy: Type.Optional(ActorUserId),
    deletedAt: Type.Optional(IsoDateTime),
  },
  { $id: 'Company' },
);
export type Company = Static<typeof Company>;
export const companySchema = createSchema(Company);

export const CompanyList = Type.Object(
  {
    companies: Type.Array(Company),
  },
  { $id: 'CompanyList' },
);
export type CompanyList = Static<typeof CompanyList>;
export const companyListSchema = createSchema(CompanyList);

export const CompanyUser = Type.Object(
  {
    userId: PartnerUserId,
    email: Type.String({ format: 'email' }),
    firstName: Type.String(),
    lastName: Type.String(),
    fullName: Type.String(),
    role: Role,
    companyKey: CompanyKey,
    lastLoggedIn: Type.Optional(IsoDateTime),
    createdAt: IsoDateTime,
    active: Type.Optional(Type.Boolean()),
    deletedAt: Type.Optional(IsoDateTime),
  },
  { $id: 'CompanyUser' },
);
export type CompanyUser = Static<typeof CompanyUser>;
export const companyUserSchema = createSchema(CompanyUser);

export const CompanyUserList = Type.Object(
  {
    users: Type.Array(CompanyUser),
  },
  { $id: 'CompanyUserList' },
);
export type CompanyUserList = Static<typeof CompanyUserList>;
export const companyUserListSchema = createSchema(CompanyUserList);

export const CreateCompanyUserInput = Type.Object(
  {
    email: Type.String({ format: 'email' }),
    firstName: Type.String({ minLength: 1 }),
    lastName: Type.String({ minLength: 1 }),
    role: Type.Optional(Role),
  },
  { $id: 'CreateCompanyUserInput' },
);
export type CreateCompanyUserInput = Static<typeof CreateCompanyUserInput>;
export const createCompanyUserInputSchema = createSchema(CreateCompanyUserInput);

export const CreateCompanyUserResponse = Type.Object(
  {
    userId: PartnerUserId,
    email: Type.String({ format: 'email' }),
    companyKey: CompanyKey,
    role: Role,
  },
  { $id: 'CreateCompanyUserResponse' },
);
export type CreateCompanyUserResponse = Static<typeof CreateCompanyUserResponse>;
export const createCompanyUserResponseSchema = createSchema(CreateCompanyUserResponse);

export const UpdateCompanyUserInput = Type.Object(
  {
    role: Type.Optional(Role),
    active: Type.Optional(Type.Boolean()),
  },
  { $id: 'UpdateCompanyUserInput' },
);
export type UpdateCompanyUserInput = Static<typeof UpdateCompanyUserInput>;
export const updateCompanyUserInputSchema = createSchema(UpdateCompanyUserInput);

export const CompanyKeyParam = Type.Object(
  {
    companyKey: CompanyKey,
  },
  { $id: 'CompanyKeyParam' },
);
export type CompanyKeyParam = Static<typeof CompanyKeyParam>;
export const companyKeyParamSchema = createSchema(CompanyKeyParam);

export const CompanyUserIdParam = Type.Object(
  {
    companyKey: CompanyKey,
    userId: PartnerUserId,
  },
  { $id: 'CompanyUserIdParam' },
);
export type CompanyUserIdParam = Static<typeof CompanyUserIdParam>;
export const companyUserIdParamSchema = createSchema(CompanyUserIdParam);
